import { z } from "zod";
import { adminBookProductionSchema } from "./admin-book-production.js";
import { adminOperationsControlSchemaVersionSchema, adminOperationsExtendedSchemaVersionSchema, adminOperatorIdSchema, adminReasonCodeSchema, adminSafeIdentifierSchema, adminSha256Schema, adminUlidSchema, } from "./admin-operations-core.js";
import { adminSubjectTypeSchema } from "./admin-operations-cases.js";
import { supportTicketBookStepSchema, supportTicketCategorySchema, supportTicketCustomerStateSchema, supportVerifiedOrderContextSchema, } from "./customer-operations-support.js";
import { customerOperationsOpaqueIdSchema } from "./customer-operations-core.js";
export const adminExactLookupCommandSchema = z
    .object({
    /** Owners may read without a case: the view is in Action history (2026-11-02). */
    caseId: adminUlidSchema.optional(),
    selector: z.discriminatedUnion("kind", [
        z
            .object({
            kind: z.literal("EMAIL"),
            value: z.string().trim().toLowerCase().email().max(254),
        })
            .strict(),
        z
            .object({
            kind: z.enum([
                "CUSTOMER_ID",
                "BOOK_ID",
                "CHECKOUT_ID",
                "ORDER_ID",
                "GIFT_ID",
                "VOUCHER_ID",
                "ENTITLEMENT_ID",
                "SUPPORT_TICKET_ID",
            ]),
            value: adminSafeIdentifierSchema,
        })
            .strict(),
        z.object({ kind: z.literal("CORRELATION_ID"), value: z.string().uuid() }).strict(),
        z.object({ kind: z.literal("VOUCHER_CODE"), value: z.string().min(8).max(64) }).strict(),
    ]),
})
    .strict();
export const adminExactLookupResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        resolved: z.boolean(),
        subjectType: adminSubjectTypeSchema.optional(),
        subjectId: adminSafeIdentifierSchema.optional(),
    })
        .strict(),
})
    .strict();
export const adminCustomerResolutionCommandSchema = z
    .object({
    /** Owners may read without a case: the view is in Action history (2026-11-02). */
    caseId: adminUlidSchema.optional(),
    selector: z.discriminatedUnion("kind", [
        z
            .object({
            kind: z.literal("EMAIL"),
            value: z.string().trim().toLowerCase().email().max(254),
        })
            .strict(),
        z.object({ kind: z.literal("CUSTOMER_ID"), value: adminSafeIdentifierSchema }).strict(),
        z
            .object({
            kind: z.literal("SUPPORT_TICKET_ID"),
            value: customerOperationsOpaqueIdSchema,
        })
            .strict(),
    ]),
})
    .strict();
export const adminCommerceInvestigationCommandSchema = z
    .object({
    /** Owners may read without a case: the view is in Action history (2026-11-02). */
    caseId: adminUlidSchema.optional(),
    selector: z.union([
        z
            .object({
            kind: z.enum(["BOOK_ID", "VOUCHER_ID", "CHECKOUT_ID", "ORDER_ID", "ENTITLEMENT_ID"]),
            value: adminSafeIdentifierSchema,
        })
            .strict(),
        // A code a customer reads out, found by its keyed hash and never recorded (owners only).
        z.object({ kind: z.literal("VOUCHER_CODE"), value: z.string().min(8).max(64) }).strict(),
    ]),
})
    .strict();
export const adminTimelineEventSchema = z
    .object({
    id: adminSafeIdentifierSchema,
    occurredAt: z.string().datetime(),
    category: z.enum([
        "ACCOUNT",
        "GIFT",
        "REDEMPTION",
        "ORDER",
        "PAYMENT",
        "BOOK",
        "WORKFLOW",
        "DELIVERY",
        "SUPPORT",
        "DATA_RIGHTS",
    ]),
    event: adminReasonCodeSchema,
    state: adminSafeIdentifierSchema,
    subjectType: adminSubjectTypeSchema,
    subjectId: adminSafeIdentifierSchema,
    correlationId: z.string().uuid().optional(),
})
    .strict();
/**
 * Which timeline entries are the same thing. A record's current state ("…_STATE") is shown
 * once per subject, newest wins, and so is a support request's receipt, which both the ticket
 * and its receipt record report; anything else is an event that happened, kept every time.
 */
export function adminTimelineEventKey(event) {
    return event.event.endsWith("_STATE") || event.event === "SUPPORT_TICKET_RECEIVED"
        ? `${event.event}#${event.subjectType}#${event.subjectId}`
        : `${event.event}#${event.id}`;
}
/** Every page loaded so far as one timeline, newest first, each entry once. */
export function mergeAdminTimeline(...pages) {
    const entries = new Map();
    for (const event of pages.flat()) {
        const key = adminTimelineEventKey(event);
        const existing = entries.get(key);
        if (!existing || event.occurredAt > existing.occurredAt)
            entries.set(key, event);
    }
    return [...entries.values()].sort((left, right) => right.occurredAt.localeCompare(left.occurredAt));
}
export const adminTimelineResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        customerId: adminSafeIdentifierSchema,
        events: z.array(adminTimelineEventSchema).max(200),
        // Where each of the three tables stopped reading, so it is longer than a one-table cursor.
        nextCursor: z.string().min(1).max(8192).optional(),
    })
        .strict(),
})
    .strict();
export const adminTimelineCommandSchema = z
    .object({
    /** Owners may read without a case: the view is in Action history (2026-11-02). */
    caseId: adminUlidSchema.optional(),
    customerId: adminSafeIdentifierSchema,
    limit: z.number().int().positive().max(200).default(50),
    cursor: z.string().min(1).max(8192).optional(),
})
    .strict();
export const adminSupportTicketDetailCommandSchema = z
    .object({
    /** Owners may read without a case: the view is in Action history (2026-11-02). */
    caseId: adminUlidSchema.optional(),
    ticketId: customerOperationsOpaqueIdSchema,
})
    .strict();
export const adminSupportTicketDetailSchema = z
    .object({
    ticketId: customerOperationsOpaqueIdSchema,
    customerId: customerOperationsOpaqueIdSchema,
    category: supportTicketCategorySchema,
    message: z.string().min(1).max(4_000),
    bookId: customerOperationsOpaqueIdSchema.optional(),
    /** The page of that book the customer asked from. */
    bookPage: z.number().int().min(1).max(999).optional(),
    /** The step of making the book the customer asked from, in their words (Stage 2, F8). */
    bookStep: supportTicketBookStepSchema.optional(),
    /** The customer's own order of that book, verified when the request was sent (G6). */
    orderContext: supportVerifiedOrderContextSchema.optional(),
    state: supportTicketCustomerStateSchema,
    assignedOperatorId: adminOperatorIdSchema.optional(),
    reviewStartedAt: z.string().datetime().optional(),
    resolvedAt: z.string().datetime().optional(),
    lastResolvedAt: z.string().datetime().optional(),
    reopenedAt: z.string().datetime().optional(),
    receivedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    version: z.number().int().positive(),
    test: z.boolean(),
    operationsEventState: z.enum(["PENDING_DELIVERY", "PUBLISHED", "NEEDS_ATTENTION"]),
})
    .strict();
export const adminSupportTicketDetailResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        ticket: adminSupportTicketDetailSchema,
    })
        .strict(),
})
    .strict();
export const adminGiftBatchModeSchema = z.enum(["TEST", "PRODUCTION"]);
export const adminGiftBatchBusinessPurposeSchema = z.enum(["BUSINESS_DEMO", "RESALE"]);
export const adminRetailerSkuSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9-]{2,63}$/u);
export const adminBrochureRevisionSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9.-]{2,63}$/u);
export const adminGiftBatchPreviewCommandSchema = z
    .object({
    caseId: adminUlidSchema,
    offerId: adminUlidSchema,
    quantity: z.number().int().positive().max(100_000),
    codePrefix: z.string().regex(/^(?:LIFE|[A-HJ-NP-Z2-9]{2,10})$/u),
    retailerSku: adminRetailerSkuSchema,
    brochureRevision: adminBrochureRevisionSchema,
    mode: adminGiftBatchModeSchema,
    businessPurpose: adminGiftBatchBusinessPurposeSchema.optional(),
})
    .strict();
export const adminGiftBatchPreviewResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        allowed: z.boolean(),
        reasonCode: adminReasonCodeSchema,
        mode: adminGiftBatchModeSchema,
        businessPurpose: adminGiftBatchBusinessPurposeSchema.optional(),
        offerId: adminUlidSchema,
        quantity: z.number().int().positive(),
        codePrefix: z.string(),
        retailerSku: adminRetailerSkuSchema,
        brochureRevision: adminBrochureRevisionSchema,
        productionLaunchState: z.enum(["GO", "NO_GO"]),
        previewDigest: adminSha256Schema,
    })
        .strict(),
})
    .strict();
/**
 * Who a voucher run is for, in the owner's words (redesign stage 1, 2026-11-02): "Acme Books".
 * Optional in the contract so batches made before it still read; the admin page requires it.
 */
export const adminGiftBatchPartnerNameSchema = z.string().trim().min(1).max(80);
/** What the owner noted when the codes were handed over ("Given to Dana at the fair"). */
export const adminGiftBatchHandedOverNoteSchema = z.string().trim().min(1).max(200);
export const adminCreateGiftBatchCommandSchema = adminGiftBatchPreviewCommandSchema.extend({
    previewDigest: adminSha256Schema,
    idempotencyKey: z.string().trim().min(8).max(128),
    partnerName: adminGiftBatchPartnerNameSchema.optional(),
});
/**
 * Marks a batch's codes as handed over to its partner or store. Setting it again keeps the first
 * time. A batch made before partner names existed can be named here, once.
 */
export const adminGiftBatchHandedOverCommandSchema = z
    .object({
    note: adminGiftBatchHandedOverNoteSchema.optional(),
    partnerName: adminGiftBatchPartnerNameSchema.optional(),
})
    .strict();
export const adminGiftExportLeaseCommandSchema = z
    .object({
    /**
     * Downloading codes is not viewing a record: it needs an open case, the run's own or the
     * downloading owner's (security review, 2026-11-02).
     */
    caseId: adminUlidSchema,
    reasonCode: adminReasonCodeSchema,
    format: z.enum(["CSV", "PRINT_PDF", "BROCHURE_ZIP"]).default("CSV"),
    commandId: z.string().uuid().optional(),
    expectedExportVersion: z.number().int().nonnegative().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.commandId === undefined) !== (value.expectedExportVersion === undefined))
        context.addIssue({
            code: "custom",
            path: ["commandId"],
            message: "An export intent must retain both its request ID and expected version.",
        });
});
export const adminGiftBatchSummarySchema = z
    .object({
    id: adminUlidSchema,
    offerId: adminUlidSchema,
    state: z.enum(["RESERVED", "GENERATING", "READY_TO_EXPORT", "EXPORTED", "ABORTED", "RETIRED"]),
    quantity: z.number().int().positive().max(100_000),
    mode: adminGiftBatchModeSchema,
    businessPurpose: adminGiftBatchBusinessPurposeSchema.optional(),
    retailerSku: adminRetailerSkuSchema.optional(),
    brochureRevision: adminBrochureRevisionSchema.optional(),
    exportDigestSha256: adminSha256Schema.optional(),
    printPacketDigestSha256: adminSha256Schema.optional(),
    printPacketPageCount: z.number().int().positive().optional(),
    printPacketState: z.enum(["READY", "CSV_ONLY"]).optional(),
    brochureZipDigestSha256: adminSha256Schema.optional(),
    brochureZipFileCount: z.number().int().positive().optional(),
    brochureZipState: z.enum(["READY", "OVERSIZE"]).optional(),
    exportedAt: z.string().datetime().optional(),
    printPacketExportedAt: z.string().datetime().optional(),
    brochureZipExportedAt: z.string().datetime().optional(),
    rawExportExpiresAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    exportVersion: z.number().int().nonnegative().optional(),
    issuedCount: z.number().int().nonnegative().optional(),
    redeemedCount: z.number().int().nonnegative().optional(),
    deliveryAttentionCount: z.number().int().nonnegative().optional(),
    partnerName: adminGiftBatchPartnerNameSchema.optional(),
    handedOverAt: z.string().datetime().optional(),
    handedOverNote: adminGiftBatchHandedOverNoteSchema.optional(),
})
    .strict();
export const adminCommerceSummarySchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    subjectType: z.enum([
        "BOOK",
        "GIFT",
        "VOUCHER",
        "REDEMPTION",
        "CHECKOUT",
        "ORDER",
        "PAYMENT",
        "ENTITLEMENT",
    ]),
    subjectId: adminSafeIdentifierSchema,
    state: adminSafeIdentifierSchema,
    environment: z.enum(["TEST", "LIVE", "UNKNOWN"]),
    relatedSubjects: z
        .array(z.object({ type: adminSubjectTypeSchema, id: adminSafeIdentifierSchema }).strict())
        .max(20),
    anomalyCodes: z.array(adminReasonCodeSchema).max(20),
    amountCents: z.number().int().nonnegative().optional(),
    currency: z.string().length(3).optional(),
    occurredAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    production: adminBookProductionSchema.optional(),
})
    .strict();
export const adminCommerceSummaryResponseSchema = z
    .object({ data: z.object({ summary: adminCommerceSummarySchema }).strict() })
    .strict();
export const adminGiftBatchResponseSchema = z
    .object({ data: z.object({ batch: adminGiftBatchSummarySchema }).strict() })
    .strict();
/** Opt-in only: old strict clients keep the unchanged batch response. No raw codes/hashes. */
export const adminGiftStockResponseSchema = z
    .object({
    data: z
        .object({
        batch: adminGiftBatchSummarySchema,
        stock: z
            .object({
            scope: z.literal("PAGE"),
            membership: z.enum(["CONSISTENT_BASE", "LEGACY_INDEX"]),
            observedFrom: z.string().datetime(),
            observedTo: z.string().datetime(),
            expectedQuantity: z.number().int().positive().max(100000),
            examined: z.number().int().nonnegative().max(50),
            wholeBatchVerified: z.boolean(),
            counts: z
                .object({
                eligible: z.number().int().nonnegative(),
                redeemed: z.number().int().nonnegative(),
                suspended: z.number().int().nonnegative(),
                revoked: z.number().int().nonnegative(),
                unavailable: z.number().int().nonnegative(),
                unknown: z.number().int().nonnegative(),
            })
                .strict(),
            rows: z
                .array(z
                .object({
                id: adminUlidSchema,
                lifecycle: z.enum([
                    "ISSUED",
                    "CREATED",
                    "INVENTORY",
                    "ALLOCATED",
                    "SHIPPED",
                    "REDEEMED",
                    "SUSPENDED",
                    "REVOKED",
                    "CANCELLED",
                    "LOST",
                    "EXPIRED",
                    "UNKNOWN",
                ]),
                eligibility: z.enum([
                    "ELIGIBLE",
                    "REDEEMED",
                    "SUSPENDED",
                    "REVOKED",
                    "UNAVAILABLE",
                    "UNKNOWN",
                ]),
                reason: adminReasonCodeSchema,
            })
                .strict())
                .max(50),
            nextCursor: z.string().max(2048).optional(),
        })
            .strict(),
    })
        .strict(),
})
    .strict();
export const adminGiftBatchSearchCommandSchema = z
    .object({
    caseId: adminUlidSchema,
    limit: z.number().int().positive().max(50).default(25),
})
    .strict();
export const adminGiftBatchSearchResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsControlSchemaVersionSchema,
        batches: z.array(adminGiftBatchSummarySchema).max(50),
        truncated: z.boolean(),
    })
        .strict(),
})
    .strict();
/**
 * Every voucher batch, newest first, with no case needed (admin assessment, 2026-10-15): the
 * case-bound search shows only the batches made in one case, and cases belong to one operator,
 * so no one could see the whole inventory. Read-only: downloads stay in the batch's own case.
 */
export const adminGiftBatchInventoryCommandSchema = z
    .object({
    limit: z.number().int().positive().max(50).default(50),
    cursor: z.string().min(1).max(512).optional(),
})
    .strict();
export const adminGiftBatchInventoryEntrySchema = adminGiftBatchSummarySchema
    .extend({ caseId: adminUlidSchema.optional() })
    .strict();
export const adminGiftBatchInventoryResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsControlSchemaVersionSchema,
        batches: z.array(adminGiftBatchInventoryEntrySchema).max(50),
        nextCursor: z.string().min(1).max(512).optional(),
    })
        .strict(),
})
    .strict();
export const adminGiftExportLeaseResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        batchId: adminUlidSchema,
        leaseId: adminUlidSchema,
        format: z.enum(["CSV", "PRINT_PDF", "BROCHURE_ZIP"]),
        downloadUrl: z.string().url(),
        expiresAt: z.string().datetime(),
    })
        .strict(),
})
    .strict();
export const adminOneOffVoucherCommandSchema = z
    .object({
    caseId: adminUlidSchema,
    recipientEmail: z.string().trim().toLowerCase().email().max(254),
    purchaseIntent: z.enum(["SELF", "GIFT"]),
    idempotencyKey: z.string().trim().min(8).max(128),
    reasonCode: adminReasonCodeSchema,
})
    .strict();
export const adminOneOffVoucherResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsControlSchemaVersionSchema,
        voucherId: adminUlidSchema,
        entitlementId: adminUlidSchema,
        state: z.literal("ISSUED"),
        deliveryState: z.enum(["SENT", "NEEDS_ATTENTION"]),
        issuedAt: z.string().datetime(),
    })
        .strict(),
})
    .strict();
/**
 * Making it right (2026-10-21): a replacement voucher for a customer whose book went wrong, sent
 * to the email they sign in with and recorded as goodwill, so its cost is counted.
 */
export const adminGoodwillReasonSchema = z.enum([
    "BOOK_FAILED",
    "PICTURES_WRONG",
    "PRINT_PROBLEM",
    "DELIVERY_PROBLEM",
    "LONG_WAIT",
    "OTHER",
]);
export const adminGoodwillVoucherCommandSchema = z
    .object({
    caseId: adminUlidSchema,
    customerId: adminUlidSchema,
    /** The book that went wrong, when there is one: it must be this customer's. */
    bookId: adminUlidSchema.optional(),
    reason: adminGoodwillReasonSchema,
    idempotencyKey: z.string().trim().min(8).max(128),
})
    .strict();
export const adminSupportGrantRequestSchema = z
    .object({
    caseId: adminUlidSchema,
    bookId: adminSafeIdentifierSchema,
    ownerId: adminSafeIdentifierSchema,
    reasonCode: adminReasonCodeSchema,
})
    .strict();
export const adminSupportGrantLookupSchema = z
    .object({
    /** Owners may read without a case: the view is in Action history (2026-11-02). */
    caseId: adminUlidSchema.optional(),
    grantId: adminSafeIdentifierSchema,
})
    .strict();
export const adminSupportDiagnosticResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        grantId: adminSafeIdentifierSchema,
        bookId: adminSafeIdentifierSchema,
        state: z.enum(["PENDING_OWNER_APPROVAL", "ACTIVE", "EXPIRED", "REVOKED"]),
        /** A withdrawn permission: when its owner may be asked again (stage 2 review, 2026-11-02). */
        askAgainAfter: z.string().datetime().optional(),
        diagnostics: z
            .object({
            workflowState: adminSafeIdentifierSchema,
            mediaCounts: z.record(z.string(), z.number().int().nonnegative()).default({}),
            issueCodes: z.array(adminReasonCodeSchema).max(20),
        })
            .strict()
            .optional(),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-operations-investigations.js.map